/**
 * @file SectionIssueList.tsx
 * @description Список замечаний блока с переходами (phase19.16, партия
 * SB120, этап 1; §7.19 фазы — «Есть замечания (N) — поля подсвечены. А где
 * исправлять?»; SB121; проект uiux-sb120-stage1 §6 — предупреждения не
 * запрещают).
 *
 * ```
 * ✕ Есть замечания (2)                              [Показать ▾]
 *   Впишите текст кнопки                             [Исправить ›]
 *   ! Меньше 12 px по бокам — на телефоне …          [Показать ›]
 * ```
 *
 * Под строкой «Есть замечания (N)» — раскрывающийся список: текст и переход
 * (вкладка → группа → элемент → поле с фокусом; сам переход — у
 * вызывающего, через `reveal` формы секции). Предупреждения оформления —
 * тем же списком, жёлтым; публикацию и сохранение они не блокируют.
 * Кнопки — 48 px на касании, 32 с мышью.
 */

import { ChevronDown, CircleAlert, CircleX } from "lucide-react";
import { useId, useState } from "react";

import { cn } from "@/lib/utils";

export interface SectionIssueItem {
  path: string;
  message: string;
  /** warning — предупреждение (жёлтым, не блокирует). */
  tone: "error" | "warning";
}

interface Props {
  items: readonly SectionIssueItem[];
  onJump: (path: string) => void;
}

const ROW_BUTTON = "inline-flex h-12 shrink-0 items-center rounded-lg px-3 text-sm font-medium outline-none hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring can-hover:h-8 can-hover:px-2";

export function SectionIssueList({ items, onJump }: Props) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  if (items.length === 0) return null;
  const errors = items.filter((i) => i.tone === "error").length;
  const warnings = items.length - errors;
  const title = errors > 0 ? `Есть замечания (${errors}) — поля подсвечены.` : `Есть предупреждения (${warnings})`;
  const extra = errors > 0 && warnings > 0 ? ` И предупреждения (${warnings}).` : "";

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-2">
        <p className={cn("type-caption min-w-0 flex-1 break-words", errors > 0 ? "text-destructive" : "text-on-surface-variant")}>
          {title}
          {extra}
        </p>
        <button type="button" aria-expanded={open} aria-controls={open ? listId : undefined} onClick={() => setOpen(!open)} className={cn(ROW_BUTTON, "text-primary")}>
          {open ? "Скрыть" : "Где исправить"}
          <ChevronDown aria-hidden className={cn("ml-1 size-4 transition-transform", open && "rotate-180")} />
        </button>
      </div>
      {open ? (
        <ul id={listId} className="flex min-w-0 flex-col gap-1">
          {items.map((item, i) => {
            const Icon = item.tone === "error" ? CircleX : CircleAlert;
            return (
              <li key={`${item.path}-${i}`} className={cn("flex min-w-0 items-center gap-2 rounded-lg px-2 py-1", item.tone === "error" ? "bg-destructive/5" : "bg-warning/10")}>
                <Icon aria-hidden className={cn("size-4 shrink-0", item.tone === "error" ? "text-destructive" : "text-warning")} />
                <span className="type-caption min-w-0 flex-1 break-words text-on-surface">{item.message}</span>
                <button type="button" className={cn(ROW_BUTTON, "text-primary")} onClick={() => onJump(item.path)} aria-label={`${item.tone === "error" ? "Исправить" : "Показать"}: ${item.message}`}>
                  {item.tone === "error" ? "Исправить ›" : "Показать ›"}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
